import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  agreementQuoteAction,
  fetchAgreementQuote,
  type AgreementQuoteApproval as ApprovalStep,
} from '@/data/serviceAgreements';
import {
  agreementApprovalTask,
  agreementApprovalWaitingOn,
  isOverrideApprovalStep,
} from '@/domain/service';
import { pendingAgreementApprovalsQueryKey } from '@/domain/home/useHomeDashboard';
import { useAgreementApprovalViewer, useApprovalPeopleNames } from '../../hooks/useAgreementApprovals';
import { useRoles } from '../../hooks/useRoles';
import useSessionStore from '../../state/session';
import { safeErrorMessage } from '../../utils/appErrors';
import './ServiceAgreements.css';

const APPROVED_STATUSES = ['approved', 'sent', 'accepted'];

const currency = (value: number) =>
  Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

function decisionLine(step: ApprovalStep, names: Record<string, string>, myAuthId: string | null) {
  const who = step.decided_by && step.decided_by === myAuthId ? 'you' : (step.decided_by && names[step.decided_by]) || 'a manager';
  const when = step.decided_at ? ` on ${new Date(step.decided_at).toLocaleString()}` : '';
  return `Approved by ${who}${when}.`;
}

/**
 * The approve step of a service agreement quote, shown above the quote document. A manager
 * approves here; the reason is required only when no approval policy covers the quote.
 */
export default function AgreementQuoteApproval({
  quoteId,
  onApproved,
}: {
  quoteId: string;
  onApproved: () => void;
}) {
  const qc = useQueryClient();
  const myAuthId = useSessionStore((s) => s.session?.user?.id ?? null);
  const viewer = useAgreementApprovalViewer();
  const { isAdmin } = useRoles();
  const query = useQuery({
    queryKey: ['agreement-quote', quoteId],
    queryFn: () => fetchAgreementQuote(quoteId),
  });
  const quote = query.data;
  const steps = React.useMemo(() => quote?.approvals ?? [], [quote]);
  const pending = quote?.status === 'approval_pending';
  const names = useApprovalPeopleNames(
    pending ? steps.map((s) => (s.status === 'pending' ? s.approver_profile_id : null)) : [],
    steps.map((s) => (s.status === 'approved' ? s.decided_by : null)),
  );
  const [reason, setReason] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  if (!quote || !(pending || (APPROVED_STATUSES.includes(quote.status) && steps.length))) {
    return query.error ? (
      <div role="alert" className="alert alert-error agreement-approval">
        {safeErrorMessage(query.error, 'Unable to load the approval')}
      </div>
    ) : null;
  }
  const task = agreementApprovalTask(steps, viewer);
  const waitingOn = pending ? agreementApprovalWaitingOn(steps, viewer, names.byProfile) : null;
  const blocked = busy || (task.needsReason && !reason.trim());
  const tierName = String(quote.snapshot?.name ?? 'Service');
  const decided = steps.filter((s) => s.status === 'approved');
  const clientId = quote.client_id;

  async function approve(event: React.FormEvent) {
    event.preventDefault();
    if (blocked) return;
    setBusy(true);
    setError('');
    try {
      await agreementQuoteAction('approve', quoteId, { reason: reason.trim() || null });
      setReason('');
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['agreement-quote', quoteId] }),
        qc.invalidateQueries({ queryKey: ['agreement-quotes', clientId] }),
        qc.invalidateQueries({ queryKey: pendingAgreementApprovalsQueryKey }),
      ]);
      onApproved();
    } catch (e) {
      setError(safeErrorMessage(e, 'Unable to approve the quote'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="agreement-surface agreement-approval" aria-labelledby="agreement-approval-title">
      <div className="agreement-approval__head">
        <div className="agreement-approval__heading">
          <span className="agreement-approval__eyebrow">Approval</span>
          <h2 id="agreement-approval-title">Service agreement quote · Version {quote.version}</h2>
          <p className="agreement-approval__meta">
            {[
              quote.client?.business_name,
              tierName,
              currency(quote.price),
              pending ? `Submitted ${new Date(quote.updated_at).toLocaleString()}` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
        <span className={`client-service-pill is-${pending ? 'approval_pending' : 'approved'}`}>
          {pending ? 'Awaiting approval' : 'Approved'}
        </span>
      </div>
      {pending ? (
        <>
          {waitingOn ? <p className="agreement-approval__text">{waitingOn}</p> : null}
          {task.canApprove ? (
            <form className="agreement-approval__form" onSubmit={approve}>
              <label className="agreement-approval__field">
                <span>
                  {task.needsReason ? 'Override reason' : 'Note'}{' '}
                  <em>· {task.needsReason ? 'required' : 'optional'}</em>
                </span>
                <textarea
                  className="control"
                  rows={3}
                  required={task.needsReason}
                  value={reason}
                  disabled={busy}
                  placeholder={
                    task.needsReason
                      ? 'Why is this quote OK to send without a policy approval?'
                      : 'Optional note for the approval history'
                  }
                  onChange={(e) => setReason(e.target.value)}
                />
              </label>
              <div className="agreement-approval__actions">
                <button type="submit" className="btn btn-primary" disabled={blocked}>
                  {busy ? 'Approving...' : 'Approve quote'}
                </button>
                {task.needsReason ? (
                  isAdmin ? (
                    <p className="agreement-approval__hint">
                      <Link to="/admin/settings?tab=quote-approvals">Add a Service agreements policy</Link> to
                      route these quotes to an approver.
                    </p>
                  ) : (
                    <p className="agreement-approval__hint">
                      An admin can add a policy in Admin Settings › Quote Approvals.
                    </p>
                  )
                ) : null}
              </div>
            </form>
          ) : null}
        </>
      ) : (
        <ul className="agreement-approval__decisions">
          {decided.map((step) => (
            <li key={step.approval_id}>
              <span>{decisionLine(step, names.byAuth, myAuthId)}</span>
              {step.reason?.trim() ? (
                <p className="agreement-approval__reason">
                  {isOverrideApprovalStep(step) ? 'Override reason: ' : 'Note: '}
                  {step.reason.trim()}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {error ? (
        <div role="alert" className="alert alert-error">
          {error}
        </div>
      ) : null}
    </section>
  );
}
